/**
 * Feed Page
 * One feed, two tabs: For You (engagement-ranked, everyone) and
 * Following (people you follow). Defaults to For You.
 * Pages in more posts on scroll (infinite scroll).
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthenticationContext';
import SinglePostDisplay from '../components/post/SinglePostDisplay';
import './FeedPageWithPosts.css';

const WORD_CATEGORIES = [100, 250, 500];
const PAGE_SIZE = 15; // must match server FEED_PAGE_SIZE

const FeedPageWithPosts = () => {
  const { token } = useAuth();
  const [tab, setTab] = useState('forYou'); // 'forYou' | 'following'
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [viewCategory, setViewCategory] = useState(null); // null = all categories
  const pageRef = useRef(1);
  const loadingMoreRef = useRef(false);
  const hasLoadedOnce = useRef(false);
  const sentinelRef = useRef(null);

  const endpoint = tab === 'following' ? '/api/posts/following' : '/api/posts/explore';

  const loadPage = useCallback(async (pageNum, replace) => {
    const params = { page: pageNum };
    if (viewCategory) params.wordCategory = viewCategory;
    const res = await axios.get(endpoint, { params });
    const batch = res.data || [];
    setPosts((prev) => (replace ? batch : [...prev, ...batch]));
    setHasMore(batch.length === PAGE_SIZE);
    return batch;
  }, [endpoint, viewCategory]);

  // Reset + load first page when tab or filter changes.
  useEffect(() => {
    let active = true;
    (async () => {
      if (!hasLoadedOnce.current) setLoading(true);
      pageRef.current = 1;
      setHasMore(true);
      try {
        await loadPage(1, true);
        hasLoadedOnce.current = true;
      } catch (error) {
        console.error('Error fetching feed:', error);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [loadPage]);

  const loadMore = useCallback(async () => {
    if (loadingMoreRef.current || !hasMore) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    const next = pageRef.current + 1;
    try {
      await loadPage(next, false);
      pageRef.current = next;
    } catch (error) {
      console.error('Error loading more posts:', error);
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [hasMore, loadPage]);

  // Infinite scroll: load more when the sentinel scrolls into view.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) loadMore(); },
      { rootMargin: '300px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [loadMore]);

  return (
    <div className="container">
      <div className="feed-header">
        <h1>Feed</h1>
        {token && (
          <Link to="/?compose=1" className="btn-create-post">
            + Respond to Prompt
          </Link>
        )}
      </div>

      <div className="feed-tabs">
        <button
          className={`feed-tab ${tab === 'forYou' ? 'active' : ''}`}
          onClick={() => setTab('forYou')}
        >
          For You
        </button>
        {token && (
          <button
            className={`feed-tab ${tab === 'following' ? 'active' : ''}`}
            onClick={() => setTab('following')}
          >
            Following
          </button>
        )}
      </div>

      <div className="feed-filters">
        <div className="category-filter-tabs">
          <button
            className={`category-tab ${viewCategory === null ? 'active' : ''}`}
            onClick={() => setViewCategory(null)}
          >
            All
          </button>
          {WORD_CATEGORIES.map((cat) => (
            <button
              key={cat}
              className={`category-tab ${viewCategory === cat ? 'active' : ''}`}
              onClick={() => setViewCategory(cat)}
            >
              {cat} words
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading feed...</div>
      ) : posts.length === 0 ? (
        <div className="no-posts">
          <p>
            {tab === 'following'
              ? "You're not following anyone yet — switch to For You to discover writers."
              : 'Nothing here yet. Be the first to write something great!'}
          </p>
        </div>
      ) : (
        <>
          <div className="posts-list">
            {posts.map((post) => (
              <SinglePostDisplay key={post._id} post={post} onUpdate={() => loadPage(1, true)} />
            ))}
          </div>
          {hasMore && <div ref={sentinelRef} className="feed-sentinel" />}
          {loadingMore && <div className="loading">Loading more...</div>}
          {!hasMore && <div className="feed-end">You're all caught up.</div>}
        </>
      )}
    </div>
  );
};

export default FeedPageWithPosts;
